/**
 * Which assignment statuses may be destroyed, and which are final.
 *
 * This exists because the previous predicate was a denylist —
 * `.neq('assignment_status', 'confirmed')` — which spared exactly one literal and
 * deleted everything else, including `completed`. The comment above it claimed the
 * opposite. That cost 1,751 completion records, 598 of which named the technician
 * who did the work.
 *
 * A denylist fails open: every status added later is deletable by default.
 * A safelist fails closed: every status added later is protected by default.
 * For a destructive operation, only the second is defensible.
 */

/**
 * The only statuses whose assignment row may be deleted.
 *
 * Deliberately excludes:
 *   - `completed` / `cancelled` / `not_there` — terminal; deleting destroys the record
 *     of the work (P3)
 *   - `confirmed` — the route is committed; existing behaviour already blocked this
 *   - `in_progress` — a technician is working on it right now
 *
 * Production holds only `completed`, `cancelled`, `assigned` and `not_there`, so this
 * deletes exactly the rows it should today, and anything new is excluded until someone
 * decides otherwise.
 */
export const REMOVABLE_ASSIGNMENT_STATUSES = ['assigned', 'pending'] as const;

/**
 * Statuses that mean the recall is finished and must not be re-opened, re-assigned,
 * or have its assignment deleted.
 *
 * `not_there` is legacy — it means the same thing as `not_available` but was written by
 * the map page as a close-out. It stays here so historical rows are still recognised as
 * terminal; the write side is being corrected separately.
 */
export const FINAL_ASSIGNMENT_STATUSES = ['completed', 'cancelled', 'not_there'] as const;

/**
 * Statuses that block removal but are not terminal — the route is committed, so the
 * recall cannot be pulled out of it, but the work has not happened yet.
 */
export const COMMITTED_ASSIGNMENT_STATUSES = ['confirmed'] as const;

/** Everything removal must refuse: terminal work plus committed routes. */
export const REMOVAL_BLOCKING_ASSIGNMENT_STATUSES = [
  ...FINAL_ASSIGNMENT_STATUSES,
  ...COMMITTED_ASSIGNMENT_STATUSES
] as const;

export type RemovableAssignmentStatus = (typeof REMOVABLE_ASSIGNMENT_STATUSES)[number];
export type FinalAssignmentStatus = (typeof FINAL_ASSIGNMENT_STATUSES)[number];

export function isFinalAssignmentStatus(status: string | null | undefined): boolean {
  return status != null && (FINAL_ASSIGNMENT_STATUSES as readonly string[]).includes(status);
}

export function isRemovalBlockingStatus(status: string | null | undefined): boolean {
  return (
    status != null && (REMOVAL_BLOCKING_ASSIGNMENT_STATUSES as readonly string[]).includes(status)
  );
}

/**
 * Statuses that mean a recall is no longer available work and must not be offered for
 * assignment, however it reached the screen.
 *
 * `confirmed` is NOT here: a confirmed recall is still live work, it just cannot be
 * pulled out of its route.
 *
 * Use this anywhere a list decides whether to show a recall as actionable. The routing
 * page previously made this decision in three places against two different sources, and
 * they disagreed — see isRecallProcessed below.
 */
export const PROCESSED_ASSIGNMENT_STATUSES = ['completed', 'cancelled', 'not_there'] as const;

/**
 * Has this recall been dealt with, so it should not appear as available work?
 *
 * Takes the status from whichever source the caller has. Callers holding a live
 * `technician_assignments` row should pass that; the `route_data` JSONB snapshot is a
 * weaker second source, because the web does not write recall status back to it on
 * completion, so it can lag.
 */
export function isRecallProcessed(status: string | null | undefined): boolean {
  return status != null && (PROCESSED_ASSIGNMENT_STATUSES as readonly string[]).includes(status);
}
